/**
 * Light / dark theme, one switch.
 *
 * The whole stylesheet is drawn from CSS custom properties (styles.css), so a
 * theme is nothing more than a different set of values for those tokens under
 * `:root[data-theme="dark"]`. This module is the small amount of behaviour that
 * can't be CSS: remember the choice, and apply it before the first paint.
 *
 * THREE STATES, on purpose:
 *   * "light" / "dark" — the viewer chose, and it is kept in localStorage so it
 *     survives a reload and a redeploy.
 *   * no stored value — follow the operating system (`prefers-color-scheme`),
 *     the same default the artifact Jonathan approved uses. The attribute is
 *     then left OFF so the `@media (prefers-color-scheme: dark)` rule in
 *     styles.css drives it and tracks the OS live.
 *
 * ⚠️ `initTheme()` runs at the TOP of main.tsx, before React mounts, so the
 * dark tokens are in place before any content paints. It is not an inline
 * <script> in index.html because the CSP (api/main.py) allows none; a module
 * script running against an empty #root is close enough that there is no flash
 * of the wrong theme in practice.
 *
 * localStorage is wrapped in try/catch throughout: a private window or blocked
 * site data must not take the toggle down, it just falls back to following the
 * OS for that session.
 */

import { useEffect, useState } from "react";

export type Theme = "light" | "dark";

const KEY = "orbit-theme";
const EVENT = "orbit-theme-change";

/** The viewer's stored choice, or null when they have never chosen. */
export function storedTheme(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

/** What the OS asks for right now. */
export function systemTheme(): Theme {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

/** The theme on screen right now: the stored choice, else the OS. */
export function effectiveTheme(): Theme {
  return storedTheme() ?? systemTheme();
}

/**
 * Apply a stored choice, or clear the attribute to follow the OS.
 *
 * Setting `data-theme` wins over the media query (see the selector pairing in
 * styles.css); removing it hands control back to `prefers-color-scheme`.
 */
function apply(theme: Theme | null): void {
  const root = document.documentElement;
  if (theme === null) root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

/** Put the remembered (or OS) theme on the page. Call once, before React. */
export function initTheme(): void {
  apply(storedTheme());
}

/** Flip light<->dark from whatever is showing, and remember it. */
export function toggleTheme(): Theme {
  const next: Theme = effectiveTheme() === "dark" ? "light" : "dark";
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // No storage (private window): the choice holds for this tab via the
    // attribute below and simply isn't remembered next time.
  }
  apply(next);
  // Let anything that renders theme-dependent (the sidebar logo and wordmark,
  // the other toggle) react without a reload.
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
  } catch {
    /* no window (SSR/tests) */
  }
  return next;
}

/**
 * The effective theme, as React state — updates when the viewer toggles or the
 * OS flips. Use it where a component must render different content per theme (a
 * light vs dark logo), which CSS alone can't swap.
 */
export function useTheme(): Theme {
  const [theme, setTheme] = useState<Theme>(() => effectiveTheme());
  useEffect(() => {
    const update = () => setTheme(effectiveTheme());
    window.addEventListener(EVENT, update);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener?.("change", update);
    return () => {
      window.removeEventListener(EVENT, update);
      mq.removeEventListener?.("change", update);
    };
  }, []);
  return theme;
}
